/**
 * ActiveCallOverlay — full-screen overlay displayed during an active voice call.
 */

'use client';

import { useState } from 'react';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  Bluetooth,
  Smartphone,
  Hash,
  Headphones,
  Check,
  ChevronDown,
} from 'lucide-react';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { AudioDeviceType } from '@/lib/twilioVoice';
import { DialPad } from './DialPad';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const STATE_LABELS: Record<string, string> = {
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  connected: '',
  reconnecting: 'Reconnecting…',
  ended: 'Call Ended',
};

const AUDIO_ROUTE_ICONS: Record<AudioDeviceType, React.ReactNode> = {
  earpiece: <Smartphone className="h-6 w-6" />,
  speaker: <Volume2 className="h-6 w-6" />,
  bluetooth: <Bluetooth className="h-6 w-6" />,
  wired: <Headphones className="h-6 w-6" />,
};

const AUDIO_ROUTE_LABELS: Record<AudioDeviceType, string> = {
  earpiece: 'Earpiece',
  speaker: 'Speaker',
  bluetooth: 'Bluetooth',
  wired: 'Headset',
};


interface ActiveCallOverlayProps {
  /** Hide the overlay without ending the call (call keeps running in the background). */
  onMinimize?: () => void;
}

export function ActiveCallOverlay({ onMinimize }: ActiveCallOverlayProps) {
  const {
    callState,
    remoteNumber,
    remoteName,
    isMuted,
    audioRoute,
    audioRouteError,
    availableAudioDevices,
    qualityWarnings,
    callDuration,
    error,
    canRedial,
    endCall,
    redial,
    toggleMute,
    selectAudioDevice,
  } = useVoiceCall();

  const [showDialPad, setShowDialPad] = useState(false);
  const [dtmfDigits, setDtmfDigits] = useState('');
  const [showAudioPicker, setShowAudioPicker] = useState(false);

  const displayName = remoteName || remoteNumber || 'Unknown';
  const stateLabel =
    callState === 'connected'
      ? formatDuration(callDuration)
      : STATE_LABELS[callState] ?? '';

  const currentRoute = audioRoute ?? 'earpiece';

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-between bg-gradient-to-b from-gray-900 to-gray-800 px-6 py-12 text-white select-none">
      {/* Minimize — hides the overlay, keeps the call running (return via the floating pill). */}
      {onMinimize && (
        <button
          onClick={onMinimize}
          aria-label="Minimize call"
          className="absolute left-4 top-[calc(max(env(safe-area-inset-top,0px),var(--android-safe-area-top,0px))+0.75rem)] flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <ChevronDown className="h-6 w-6" />
        </button>
      )}

      {/* Quality warning banner */}
      {audioRouteError && callState !== 'ended' && (
        <div role="status" className="w-full rounded-lg bg-amber-500/20 px-4 py-2 text-center text-sm text-amber-200">
          {audioRouteError}
        </div>
      )}
      {qualityWarnings.length > 0 && (
        <div className="w-full rounded-lg bg-yellow-500/20 px-4 py-2 text-center text-sm text-yellow-300">
          Connection quality: {qualityWarnings.join(', ')}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="w-full rounded-lg bg-red-500/20 px-4 py-2 text-center text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Contact info */}
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        {/* Avatar placeholder */}
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-600 text-4xl font-semibold">
          {displayName.charAt(0).toUpperCase()}
        </div>

        <p className="text-2xl font-semibold">{displayName}</p>
        {remoteName && remoteNumber && !remoteNumber.startsWith('contact:') && (
          <p className="text-sm text-gray-400">{remoteNumber}</p>
        )}

        {/* Status / timer */}
        <p className="mt-1 text-base text-gray-300">{stateLabel}</p>
      </div>

      {/* Dial pad (in-call DTMF) */}
      {showDialPad && (
        <div className="w-full max-w-xs">
          {dtmfDigits && (
            <div className="mb-2 rounded-xl bg-white/10 px-4 py-3 text-center">
              <span className="text-2xl font-mono tracking-widest text-white">
                {dtmfDigits}
              </span>
            </div>
          )}
          <DialPad dtmfMode onDigitPressed={(d) => setDtmfDigits((prev) => prev + d)} />
        </div>
      )}

      {/* Audio device picker popup */}
      {showAudioPicker && (
        <div className="absolute bottom-48 left-1/2 -translate-x-1/2 w-56 rounded-2xl bg-gray-700 shadow-2xl overflow-hidden">
          {availableAudioDevices.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">No devices available</div>
          ) : (
            availableAudioDevices.map((device) => (
              <button
                key={device.id ?? device.type}
                onClick={() => {
                  selectAudioDevice(device.type, device.id);
                  setShowAudioPicker(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-white/10 transition-colors"
              >
                <span className="flex-shrink-0">{AUDIO_ROUTE_ICONS[device.type]}</span>
                <span className="flex-1">{device.name}</span>
                {currentRoute === device.type && (
                  <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      )}

      {/* Secondary controls */}
      <div className="mb-6 flex w-full max-w-xs items-center justify-around">
        {/* Mute */}
        <button
          onClick={toggleMute}
          className={`flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-full transition-colors ${
            isMuted ? 'bg-white text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
          <span className="text-[10px]">{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>

        {/* Audio route picker */}
        <button
          onClick={() => setShowAudioPicker((v) => !v)}
          className={`flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-full transition-colors ${
            showAudioPicker ? 'bg-white text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          aria-label="Audio output"
        >
          {AUDIO_ROUTE_ICONS[currentRoute]}
          <span className="text-[10px]">{AUDIO_ROUTE_LABELS[currentRoute]}</span>
        </button>

        {/* Keypad */}
        <button
          onClick={() => { setShowDialPad((v) => !v); setDtmfDigits(''); }}
          className={`flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-full transition-colors ${
            showDialPad ? 'bg-white text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          aria-label="Keypad"
        >
          <Hash className="h-6 w-6" />
          <span className="text-[10px]">Keypad</span>
        </button>
      </div>

      {/* End call — or, when the call dropped uncleanly (network switch), Dismiss + Redial side by side */}
      {callState === 'ended' && canRedial ? (
        <div className="flex items-center gap-10">
          {/* Dismiss — end on the app side (closes the overlay + call bar immediately) */}
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={endCall}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg transition-transform active:scale-95"
              aria-label="Dismiss call"
            >
              <PhoneOff className="h-7 w-7 text-white" />
            </button>
            <span className="text-xs text-gray-300">Dismiss</span>
          </div>
          {/* Redial the same number */}
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={redial}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg transition-transform active:scale-95"
              aria-label="Redial"
            >
              <Phone className="h-7 w-7 text-white" />
            </button>
            <span className="text-xs text-gray-300">Redial</span>
          </div>
        </div>
      ) : (
        <button
          onClick={endCall}
          disabled={callState === 'ended'}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 shadow-lg transition-transform active:scale-95 disabled:opacity-50"
          aria-label="End call"
        >
          <PhoneOff className="h-7 w-7 text-white" />
        </button>
      )}

      {/* Spacer */}
      <Phone className="mt-8 h-4 w-4 text-transparent" aria-hidden />
    </div>
  );
}
